import "@/App.css";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Carousel } from "@/components/Carousel";

const PANETTONE_LOGO =
  "https://customer-assets.emergentagent.com/job_chiara-premium-cafe/artifacts/x0utwtxk_ChatGPT%20Image%2012%20lug%202026%2C%2020_47_51.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Home = () => {
  return (
    <div className="App sc-backdrop min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-28 sm:pt-32 pb-20">
        {/* Hero */}
        <section
          data-testid="hero-section"
          className="max-w-4xl mx-auto px-6 text-center mb-10 sm:mb-14"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            initial="hidden"
            animate="show"
            className="text-[0.72rem] sm:text-sm uppercase tracking-[0.4em] text-[#800020] font-medium"
          >
            Santa Chiara · Caffè & Gelateria
          </motion.p>

          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A]"
          >
            A little corner of{" "}
            <span className="italic gold-text">la dolce vita</span>.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            className="font-serif-soft mt-6 text-lg sm:text-xl text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed"
          >
            Every morning we churn our gelato in small batches, the old Italian
            way — slow, dense and velvety, made only with whole milk,
            sun-ripened fruit and no shortcuts. Pistachio from Bronte, dark
            chocolate that lingers, wild strawberry that tastes like July.
          </motion.p>

          <motion.p
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="font-sans mt-4 text-base text-[#4A4A4A]/90 max-w-2xl mx-auto leading-relaxed"
          >
            Pair it with a proper espresso, a warm cornetto, or take a scoop to
            go from our vintage cart. Come find us — your first taste is the
            reason you'll come back. Drag the cards below to explore.
          </motion.p>
        </section>

        {/* Interactive carousel */}
        <motion.div
          variants={fadeUp}
          custom={4}
          initial="hidden"
          animate="show"
        >
          <Carousel />
        </motion.div>

        {/* Panettone Festival */}
        <motion.section
          data-testid="panettone-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl mx-auto px-6 mt-24 sm:mt-32"
        >
          <div className="text-center mb-8">
            <div className="gold-rule w-16 mx-auto" />
          </div>

          <a
            href="https://www.newyorkpanettonefestival.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="panettone-link"
            className="group block rounded-[28px] bg-white sc-card-shadow border border-[#D4AF37]/25 overflow-hidden transition-transform duration-500 hover:-translate-y-2"
          >
            <div className="grid md:grid-cols-2 items-center">
              <div className="p-8 sm:p-10 flex items-center justify-center bg-white">
                <img
                  src={PANETTONE_LOGO}
                  alt="I Love NY — New York Panettone Festival"
                  className="w-full max-w-[380px] h-auto transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-8 sm:p-10 md:pr-14">
                <h2 className="font-display text-3xl sm:text-4xl text-[#800020] leading-tight">
                  New York Panettone Festival
                </h2>
                <p className="font-serif-soft mt-4 text-lg text-[#4A4A4A] leading-relaxed">
                  A celebration of the great Italian classic, reimagined in the
                  heart of New York. Join Santa Chiara and the city's finest
                  bakers for a season of artisan panettone, tastings and pure
                  festive joy.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-white bg-[#800020] group-hover:bg-[#D4AF37] group-hover:text-[#800020] px-6 py-3 rounded-full transition-colors duration-300">
                  Discover the festival
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                </span>
              </div>
            </div>
          </a>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

function App() {
  return <Home />;
}

export default App;
